import { useState, useEffect } from "react";
import {
  Box,
  Input,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  VStack,
  Fade,
  Flex,
  useToast,
  useColorModeValue,
  Icon,
  Divider,
  Spinner,
  Tag,
  Tooltip,
} from "@chakra-ui/react";
import { FaUpload, FaExclamationTriangle, FaCheckCircle, FaDownload } from "react-icons/fa";
import GlassButton from "../pressets/GlassButton";
import {
  getMonthlyByYear,
  getYearlyByYear,
  upsertYearly,
} from "@/api/incomeService";

const transformYearlyFromBackend = (backendData) => {
  const frontData = {};
  Object.keys(backendData).forEach((key) => {
    if (key.startsWith("i")) {
      const code = parseInt(key.substring(1), 10);
      frontData[code] = backendData[key].amount;
    }
  });
  return frontData;
};

const transformYearlyForBackend = (year, frontData) => {
  const codeKeyMap = {
    301: "sueldo",
    303: "otros_gravados",
    305: "utilidades",
    307: "otros_empleadores",
    311: "decimo_tercero",
    313: "decimo_cuarto",
    315: "fondos_reserva",
    317: "otros_no_gravados",
  };

  const backendObj = { year };

  Object.keys(codeKeyMap).forEach((code) => {
    const key = codeKeyMap[code];
    backendObj["i" + code] = { key, amount: frontData[Number(code)] || 0 };
  });

  return backendObj;
};

export default function SRITableNew({ selectedYear, onDownload, isDownloading }) {
  const [yearly, setYearly] = useState(null);
  const [months, setMonths] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [data, setData] = useState({});
  const [fileName, setFileName] = useState("");

  const toast = useToast();

  const INCOME_KEYS = [
    { key: "sueldo", label: "Sueldo", category: "saldo", code: 301 },
    {
      key: "comisiones",
      label: "Comisiones/Horas extra",
      category: "gravado",
      code: 301,
    },
    {
      key: "utilidades",
      label: "Utilidades/Anticipo de utilidades",
      category: "gravado",
      code: 305,
    },
    {
      key: "bonos_mayores",
      label: "Bonos mayores del 20% del ingreso gravado IESS",
      category: "gravado",
      code: 303,
    },
    {
      key: "otros_gravados",
      label: "Otros ingresos gravados",
      category: "gravado",
      code: 307,
    },
    {
      key: "fondos_reserva",
      label: "Fondos de reserva",
      category: "no_gravado",
      code: 315,
    },
    {
      key: "decimo_tercero",
      label: "Décimo tercero",
      category: "no_gravado",
      code: 311,
    },
    {
      key: "decimo_cuarto",
      label: "Décimo cuarto",
      category: "no_gravado",
      code: 313,
    },
    {
      key: "otros_no_gravados",
      label: "Otros ingresos no gravados",
      category: "no_gravado",
      code: 317,
    },
    {
      key: "bonos_menores",
      label: "Bonos menores del 20% del ingreso gravado IESS",
      category: "no_gravado",
      code: 317,
    },
    {
      key: "indemnizaciones",
      label: "Indemnizaciones",
      category: "no_gravado",
      code: 317,
    },
  ];

  const sriColor = "teal";
  const platformColor = "purple";

  const diffBg = "whiteAlpha.200";
  const diffBorder = "orange.300";
  const diffTextColor = "orange.200";
  const warningColor = "orange.500";
  const okColor = "green.500";

  const textColor = "white";
  const tableBgColor = "glass.strong";
  const tableRowColor = "glass.strong";
  const tableHeaderColor = "white";

  const codeNames = {
    301: "SUELDOS, SALARIOS Y OTROS INGRESOS GRAVADOS DE IMPUESTO A LA RENTA (materia gravada de la seguridad social)",
    303: "OTROS INGRESOS GRAVADOS DE IMPUESTO A LA RENTA (materia no gravada de la seguridad social)",
    305: "PARTICIPACIÓN UTILIDADES",
    307: "INGRESOS GRAVADOS GENERADOS CON OTROS EMPLEADORES",
    311: "DÉCIMO TERCER SUELDO",
    313: "DÉCIMO CUARTO SUELDO",
    315: "FONDO DE RESERVA",
    317: "OTROS INGRESOS EN RELACIÓN DE DEPENDENCIA QUE NO CONSTITUYEN MATERIA GRAVADA DE IMPUESTO A LA RENTA",
  };

  const fmt = (n) =>
    (isFinite(n) ? Number(n) : 0).toLocaleString("es-EC", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    });

  const handleUpsertYearly = async (data) => {
    if (!selectedYear) return;
    setIsSaving(true);
    try {
      const payload = transformYearlyForBackend(selectedYear, data);
      await upsertYearly(payload);

      toast({ title: "Resumen anual guardado", status: "success" });
    } catch (e) {
      console.error(e);
      toast({ title: "Error al guardar resumen anual", status: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleFileChange = async (e) => {
    setYearly(null);
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "Archivo demasiado grande",
        description: "El archivo no puede superar los 5MB",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const typedArray = new Uint8Array(reader.result);

        const pdfjsLib = await import("pdfjs-dist/webpack");
        pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

        const pdf = await pdfjsLib.getDocument(typedArray).promise;
        const totals = {};

        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          const textContent =
            content.items
              .map((it) => it.str)
              .join(" ")
              .replace(/\s+/g, " ") + "\n";

          const regex = /(\d{3})\s+([+-]?\s*\$\s*[\d,.]+)/g;
          let match;
          while ((match = regex.exec(textContent)) !== null) {
            const code = parseInt(match[1], 10);
            const valueStr = match[2].replace(/[\s$]/g, "").replace(/,/g, "");
            const value = parseFloat(valueStr);

            if (codeNames[code]) {
              totals[code] =
                (totals[code] || 0) + (isFinite(value) ? value : 0);
            }
          }
        }

        if (Object.keys(totals).length === 0) {
          throw new Error("El PDF no cumple con el formato esperado.");
        }

        handleUpsertYearly(totals);
        setData(totals);

        toast({
          title: "Carga Exitosa",
          description: `Datos cargados desde ${file.name}.`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } catch (err) {
        console.error(err);
        toast({
          title: "Error al procesar PDF",
          description:
            err.message || "El archivo no tiene el formato esperado.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setData({});
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const monthsTotalsByCode = INCOME_KEYS.reduce((acc, { key, code }) => {
    const sumForKey = months.reduce((s, m) => s + (m?.[key]?.amount || 0), 0);
    acc[code] = (acc[code] || 0) + sumForKey;
    return acc;
  }, {});

  const effectiveData = yearly !== null ? yearly : data;

  const getDifference = (code) => {
    const sriAmount = effectiveData[code] || 0;
    const platformAmount = monthsTotalsByCode[code] || 0;
    return sriAmount - platformAmount;
  };

  const isDifferent = (code) => {
    return Math.abs(getDifference(code)) > 0.005;
  };

  const codesOrderPdf = Object.keys(effectiveData)
    .map((c) => Number(c))
    .filter((c) => codeNames[c])
    .sort((a, b) => a - b);

  const uniqueCodes = Array.from(new Set(INCOME_KEYS.map((k) => k.code)))
    .filter((c) => codeNames[c])
    .sort((a, b) => a - b);

  const allRelevantCodes = Array.from(
    new Set([...codesOrderPdf, ...uniqueCodes])
  ).sort((a, b) => a - b);

  const cellStyle = {
    whiteSpace: "normal",
    wordWrap: "break-word",
    maxW: "300px",
    fontSize: "xs",
    color: textColor,
  };

  const loadMonths = async (year) => {
    try {
      setIsLoading(true);
      const data = await getMonthlyByYear(year);
      setMonths(data || []);
    } catch (e) {
      console.error(e);
      toast({ title: "Error al obtener data mensual", status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const loadSelectedYear = async (year) => {
    try {
      setIsLoading(true);
      const data = await getYearlyByYear(year);
      if (data && Object.keys(data).length > 0) {
        setYearly(transformYearlyFromBackend(data));
      } else {
        setYearly(null);
      }
    } catch (e) {
      console.error(e);
      toast({ title: "Error al obtener resumen anual", status: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedYear) return;
    loadMonths(selectedYear);
    loadSelectedYear(selectedYear);
  }, [selectedYear]);

  useEffect(() => {
    if (yearly) {
      setData(yearly);
    } else {
      setData({});
    }
  }, [yearly]);

  const renderComparisonTable = (codesList) => {
    const headerBg = "whiteAlpha.200";

    return (
      <Box
        borderRadius="lg"
        shadow="xl"
        border="1px solid"
        borderColor={"whiteAlpha.200"}
        overflowX="auto"
        bg={tableBgColor}
        w="full"
        maxW="100%"
      >
        <TableContainer overflowX="auto">
          <Table variant="glass" size="sm" minW="640px">
            <Thead>
              <Tr bg={headerBg}>
                <Th w="70px" color={tableHeaderColor} borderRight="1px solid">
                  Código
                </Th>
                <Th color={tableHeaderColor} minW="250px">
                  Concepto
                </Th>
                <Th
                  isNumeric
                  w="140px"
                  color={`${sriColor}.600`}
                  fontWeight="bold"
                >
                  Formulario 107 del SRI
                </Th>
                <Th
                  isNumeric
                  w="140px"
                  color={`${platformColor}.600`}
                  fontWeight="bold"
                >
                  ConTex
                </Th>
                <Th
                  isNumeric
                  w="140px"
                  color={tableHeaderColor}
                  fontWeight="bold"
                  borderLeft="1px solid"
                >
                  Diferencia
                </Th>
                <Th w="30px" textAlign="center" color={tableHeaderColor}></Th>
              </Tr>
            </Thead>
            <Tbody>
              {codesList.map((code) => {
                const sriAmount = effectiveData[code] || 0;
                const platformAmount = monthsTotalsByCode[code] || 0;
                const difference = getDifference(code);
                const hasDiff = isDifferent(code);

                const isMissingFromSRI =
                  sriAmount === 0 && platformAmount !== 0;
                const isMissingFromPlatform =
                  platformAmount === 0 && sriAmount !== 0;

                const rowBg = hasDiff ? diffBg : tableRowColor;
                const rowBorder = hasDiff ? `4px solid ${diffBorder}` : "none";
                const rowTextColor = hasDiff ? diffTextColor : textColor;

                return (
                  <Tr
                    key={code}
                    bg={rowBg}
                    borderLeft={rowBorder}
                    color={rowTextColor}
                    _hover={{
                      bg: hasDiff
                        ? "whiteAlpha.200"
                        : "whiteAlpha.200",
                    }}
                  >
                    <Td
                      fontWeight="semibold"
                      borderRight="1px solid"
                      borderColor={"whiteAlpha.200"}
                    >
                      {code}
                    </Td>
                    <Td {...cellStyle}>{codeNames[code]}</Td>
                    <Td
                      isNumeric
                      color={hasDiff ? `${sriColor}.700` : textColor}
                      fontWeight={hasDiff ? "bold" : "normal"}
                    >
                      {fmt(sriAmount)}
                    </Td>
                    <Td
                      isNumeric
                      color={hasDiff ? `${platformColor}.700` : textColor}
                      fontWeight={hasDiff ? "bold" : "normal"}
                    >
                      {fmt(platformAmount)}
                    </Td>
                    <Td
                      isNumeric
                      fontWeight={hasDiff ? "extrabold" : "normal"}
                      color={
                        hasDiff
                          ? difference > 0
                            ? "red.500"
                            : "green.500"
                          : textColor
                      }
                      borderLeft="1px solid"
                      borderColor={"whiteAlpha.200"}
                    >
                      {fmt(difference)}
                    </Td>
                    <Td textAlign="center">
                      {hasDiff && (
                        <Tooltip
                          label={`¡Discrepancia! Diferencia: ${fmt(
                            difference
                          )}`}
                          aria-label="Discrepancia"
                        >
                          <Icon
                            as={FaExclamationTriangle}
                            color={warningColor}
                            boxSize={4}
                            cursor="help"
                          />
                        </Tooltip>
                      )}
                      {!hasDiff &&
                        !isMissingFromPlatform &&
                        !isMissingFromSRI && (
                          <Icon
                            as={FaCheckCircle}
                            color={okColor}
                            boxSize={4}
                            title="Montos Coinciden"
                          />
                        )}
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <VStack
        spacing={6}
        maxW="1200px"
        mx="auto"
        mt={10}
        w="full"
        align="center"
        py={20}
      >
        <Spinner size="xl" color="teal.600" />
        <Text fontSize="lg" color={textColor}>
          Cargando datos anuales de {selectedYear}...
        </Text>
      </VStack>
    );
  }

  if (codesOrderPdf.length === 0 && !yearly) {
    return (
      <VStack
        spacing={6}
        maxW="1200px"
        mx="auto"
        mt={10}
        w="full"
        align="stretch"
      >
        <Flex
          direction="column"
          align="center"
          justify="center"
          border="3px dashed"
          borderColor="teal.400"
          borderRadius="lg"
          p={10}
          minH="300px"
          textAlign="center"
          bg="whiteAlpha.300"
        >
          <Input
            type="file"
            accept="application/pdf"
            onChange={handleFileChange}
            display="none"
            id="pdf-input"
          />
          <GlassButton
            as="label"
            htmlFor="pdf-input"
            leftIcon={<FaUpload />}
            colorScheme="teal"
            size="lg"
            isLoading={isSaving}
          >
            Seleccionar Formulario 107 del SRI (PDF)
          </GlassButton>
          <Text mt={4} fontSize="md" color="gray.700" maxW="400px">
            Una vez finalizado el año, puedes subir el
            <strong> Formulario 107 del SRI</strong> del año
            <strong> {selectedYear}</strong> para comparar los datos.
          </Text>
        </Flex>
      </VStack>
    );
  }

  return (
    <VStack
      spacing={6}
      maxW="1200px"
      mx="auto"
      mt={10}
      w="full"
      align="stretch"
    >
      <Text fontSize="2xl" fontWeight="bold" color="gray.700" textAlign="center">
        Comparación de Ingresos Anuales
      </Text>

      <Flex justify="center" gap={4} wrap="wrap">
        <Tag size="lg" colorScheme={sriColor} variant="solid">
          Formulario 107 del SRI
        </Tag>
        <Tag size="lg" colorScheme={platformColor} variant="solid">
          Datos Ingresados en ConTex
        </Tag>
      </Flex>

      <Fade in={true}>
        {renderComparisonTable(allRelevantCodes)}

        <Box textAlign="center" mt={6}>
          <Flex justify="center" align="center" gap={4} my={4}>
            <Input
              type="file"
              accept="application/pdf"
              onChange={handleFileChange}
              display="none"
              id="pdf-input-overwrite"
            />
            <GlassButton
              as="label"
              htmlFor="pdf-input-overwrite"
              leftIcon={<FaUpload />}
              colorScheme="teal"
              size="md"
              isLoading={isSaving}
            >
              Cargar/Sobrescribir Formulario 107
            </GlassButton>
            <GlassButton
              leftIcon={<FaDownload />}
              colorScheme="green"
              size="md"
              onClick={onDownload}
              isLoading={isDownloading}
            >
              Descargar Declaración
            </GlassButton>
          </Flex>
        </Box>
      </Fade>
    </VStack>
  );
}
